/** Console v0.5 Review inbox: tabs, the words for each refusal, and which actions to offer (design brief v0.5 s.6;
 * session A's contract s.12). Pure -- tested with Node (web/console/test/v05-inbox.test.ts). The server checks every
 * action again; `permissions` only decides what's offered and lets the screen say why Approve is off. */

import type { ApproveBlocked, InboxTab, ItemDetail, ReleaseBlocked } from "./items-api.js";

export const INBOX_TABS: ReadonlyArray<{ id: InboxTab; label: string }> = [
  { id: "awaiting_you", label: "Awaiting you" },
  { id: "exceptions", label: "Exceptions" },
  { id: "requested", label: "Requested" },
  { id: "done", label: "Done" },
];

export const INBOX_COPY = {
  title: "Review inbox",
  intro: "Work Preekie has prepared for you. Check it, then approve it or send it back.",
  emptyTitle: "All clear",
  emptyBody: "Nothing here needs you right now.",
  pick: "Choose a piece of work to see it here.",
  why: "Why Preekie did this",
  evidence: "Evidence",
  approved: "Approved by you. Someone who can lodge releases it from Done.",
  reviewed: "Marked as reviewed. A different person approves it.",
  sentBack: "Sent back with your note. It's now with Requested.",
  released: "Released for lodgement.",
  sendBackTitle: "Send back with a note",
  sendBackHint: "Say what needs to change. The note is kept with this piece of work.",
  noteRequired: "Write a note of up to 1,000 characters.",
  shortcuts: "Shortcuts: A approve · J K next, previous",
} as const;

export function tabFromQuery(search: string): InboxTab {
  const t = new URLSearchParams(search).get("tab");
  return INBOX_TABS.some((x) => x.id === t) ? (t as InboxTab) : "awaiting_you";
}

/** Why Approve is off, in words that say what to do (A's approve_blocked codes). */
export function approveBlockedText(code: ApproveBlocked | null): string | null {
  switch (code) {
    case null:
      return null;
    case "capability_required":
      return "You can't approve this: approving needs the Approve capability. An account admin can give it to you on Team.";
    case "self_approval_not_allowed":
      return "A different person must approve this, because you prepared or reviewed it.";
    case "not_awaiting_review":
      return "Only work that's prepared or awaiting review can be approved.";
    case "service_team_forbidden":
      return "The Preekie service team can't approve or lodge. Only your team can.";
  }
}

export interface Offer {
  approve: boolean; // shown (enabled or with its reason)
  approveEnabled: boolean;
  review: boolean;
  sendBack: boolean;
  release: boolean; // shown (enabled or with its reason)
  releaseEnabled: boolean;
}

/** Why Release is off, in words (Prabash's release rule: the preparer can't release; the approver may). */
export function releaseBlockedText(code: ReleaseBlocked | null | undefined): string | null {
  switch (code) {
    case "preparer_cannot_release":
      return "A different person must release this, because you prepared it.";
    case "lodge_not_permitted":
      return "Releasing needs the Lodge capability. An account admin can give it to you on Team.";
    case "not_approved":
      return "Only approved work can be released.";
    case "service_team_forbidden":
      return "The Preekie service team can't approve or lodge. Only your team can.";
    default:
      return null;
  }
}

/** What the detail pane offers for an item. Approve is shown (perhaps disabled, with its reason) whenever the item is
 * waiting for a decision, so a person who can't approve still learns why; the others only when allowed. */
export function offerFor(item: Pick<ItemDetail, "status_label" | "permissions">): Offer {
  const waiting = item.status_label === "Prepared" || item.status_label === "Awaiting review";
  const p = item.permissions;
  return {
    approve: waiting,
    approveEnabled: waiting && p.can_approve,
    review: waiting && p.can_review,
    sendBack: waiting && p.can_send_back,
    // Shown for approved work to someone who could lodge it -- disabled, with the reason, when the rule says no.
    release: item.status_label === "Approved" && (p.can_release || (!!p.release_blocked && p.release_blocked !== "lodge_not_permitted" && p.release_blocked !== "service_team_forbidden")),
    releaseEnabled: item.status_label === "Approved" && p.can_release,
  };
}

/** J / K move through the list; A approves (only when Approve is enabled). Ignored while typing or in a dialog. */
export function shortcut(key: string, inField: boolean): "next" | "previous" | "approve" | null {
  if (inField) return null;
  switch (key) {
    case "j":
    case "J":
      return "next";
    case "k":
    case "K":
      return "previous";
    case "a":
    case "A":
      return "approve";
    default:
      return null;
  }
}

export function noteProblem(note: string): string | null {
  const n = note.trim().length;
  return n >= 1 && n <= 1000 ? null : INBOX_COPY.noteRequired;
}
